import React, { FC, useEffect, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import DarkThemeProviderWrapper from '../../../DarkThemeProviderWrapper';
import Typography from '@material-ui/core/Typography';
import { RouteComponentProps } from 'react-router';
import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import gsap from 'gsap';
import clsx from 'clsx';

const mockData = [
  {
    hospitalName: 'University Ambulatory SC',
    fcots: 93,
    turnover: 22,
    utilization: 88,
    cases: 1450,
  },
  {
    hospitalName: 'Memorial Medical Center',
    fcots: 87,
    turnover: 25,
    utilization: 92,
    cases: 1680,
  },
  {
    hospitalName: "St. Mary's Regional Hospital",
    fcots: 91,
    turnover: 19,
    utilization: 85,
    cases: 1320,
  },
  {
    hospitalName: 'Central Valley Surgery Center',
    fcots: 78,
    turnover: 28,
    utilization: 76,
    cases: 980,
  },
  {
    hospitalName: 'Riverside Orthopedic Institute',
    fcots: 95,
    turnover: 18,
    utilization: 94,
    cases: 1820,
  },
  {
    hospitalName: 'Lakeside Ambulatory Clinic',
    fcots: 82,
    turnover: 24,
    utilization: 81,
    cases: 1150,
  },
  {
    hospitalName: 'Metropolitan Surgical Associates',
    fcots: 89,
    turnover: 21,
    utilization: 87,
    cases: 1590,
  },
  {
    hospitalName: 'Coastal Healthcare Partners',
    fcots: 85,
    turnover: 26,
    utilization: 79,
    cases: 1240,
  },
  {
    hospitalName: 'Summit Specialty Surgery',
    fcots: 92,
    turnover: 20,
    utilization: 90,
    cases: 1720,
  },
  {
    hospitalName: 'Parkview Outpatient Center',
    fcots: 80,
    turnover: 27,
    utilization: 83,
    cases: 1090,
  },
  {
    hospitalName: 'Northside Medical Plaza',
    fcots: 88,
    turnover: 23,
    utilization: 86,
    cases: 1410,
  },
  {
    hospitalName: 'Greenwood Surgery Pavilion',
    fcots: 94,
    turnover: 17,
    utilization: 91,
    cases: 1880,
  },
  {
    hospitalName: 'Westside Surgical Center',
    fcots: 86,
    turnover: 24,
    utilization: 82,
    cases: 1380,
  },
  {
    hospitalName: 'Highland Ambulatory Care',
    fcots: 90,
    turnover: 21,
    utilization: 89,
    cases: 1620,
  },
  {
    hospitalName: 'Bayshore Medical Institute',
    fcots: 77,
    turnover: 29,
    utilization: 74,
    cases: 890,
  },
  {
    hospitalName: 'Eastgate Surgery Pavilion',
    fcots: 93,
    turnover: 19,
    utilization: 92,
    cases: 1750,
  },
  {
    hospitalName: 'Valley View Healthcare',
    fcots: 84,
    turnover: 25,
    utilization: 80,
    cases: 1280,
  },
  {
    hospitalName: 'Crescent Moon Surgical',
    fcots: 91,
    turnover: 20,
    utilization: 88,
    cases: 1540,
  },
  {
    hospitalName: 'Pinecrest Medical Group',
    fcots: 79,
    turnover: 26,
    utilization: 77,
    cases: 1050,
  },
  {
    hospitalName: 'Horizon Specialty Center',
    fcots: 96,
    turnover: 16,
    utilization: 95,
    cases: 1920,
  },
  {
    hospitalName: 'Maple Grove Surgery',
    fcots: 88,
    turnover: 22,
    utilization: 84,
    cases: 1460,
  },
  {
    hospitalName: 'Silverstone Ambulatory SC',
    fcots: 81,
    turnover: 27,
    utilization: 78,
    cases: 1120,
  },
  {
    hospitalName: 'Clearwater Surgical Partners',
    fcots: 92,
    turnover: 18,
    utilization: 90,
    cases: 1690,
  },
  {
    hospitalName: 'Oakmont Medical Plaza',
    fcots: 83,
    turnover: 24,
    utilization: 81,
    cases: 1210,
  },
];

const Stat = ({
  title,
  value,
  indication,
  onClick,
}: {
  title: string;
  value: number;
  indication?: 'good' | 'warning' | 'severe';
  onClick?: () => void;
}) => {
  const classes = useStyles();
  const [displayValue, setDisplayValue] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isGlowing, setIsGlowing] = useState(false);

  useEffect(() => {
    // Animate the number from 0 to value
    gsap.to(
      { val: 0 },
      {
        val: value,
        duration: 1,
        ease: 'power2.out',
        delay: 0.2, // Start after initial page load
        onUpdate: function () {
          setDisplayValue(Math.round(this.targets()[0].val));
        },
        onComplete: () => {
          setIsComplete(true);
          setIsGlowing(true);
          // Remove glow after brief moment
          setTimeout(() => setIsGlowing(false), 400);
        },
      }
    );
  }, [value]);

  return (
    <Box flex={1} textAlign="center" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <Typography color="textSecondary" variant="subtitle1">
        {title}
      </Typography>
      <Typography
        variant="h2"
        className={clsx({
          [classes.statGood]: isComplete && indication === 'good',
          [classes.statWarning]: isComplete && indication === 'warning',
          [classes.statSevere]: isComplete && indication === 'severe',
        })}
        style={{
          color: isComplete ? undefined : 'rgba(255, 255, 255, 0.5)',
          textShadow: isGlowing ? '0 0 30px currentColor, 0 0 60px currentColor' : 'none',
          transition: 'text-shadow 0.3s ease',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

const StatBox = ({
  title,
  average,
  best,
  worst,
  bestIndication,
  worstIndication,
  onBestClick,
  onWorstClick,
}: {
  title: string;
  average: number;
  best: number;
  worst: number;
  bestIndication?: 'good' | 'warning' | 'severe';
  worstIndication?: 'good' | 'warning' | 'severe';
  onBestClick?: () => void;
  onWorstClick?: () => void;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.statBox}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Box display="flex">
        <Stat title="Average" value={average} />
        <Stat title="Best" value={best} indication={bestIndication} onClick={onBestClick} />
        <Stat title="Worst" value={worst} indication={worstIndication} onClick={onWorstClick} />
      </Box>
    </Box>
  );
};

const SmallStat = ({
  title,
  value,
  indication,
  delay = 0,
}: {
  title: string;
  value: string;
  indication: 'good' | 'warning' | 'severe';
  delay?: number;
}) => {
  const classes = useStyles();
  const [displayValue, setDisplayValue] = useState(value);
  const [isComplete, setIsComplete] = useState(false);
  const [isGlowing, setIsGlowing] = useState(false);

  useEffect(() => {
    // Extract numeric value from string (e.g., "93%" -> 93, "22 min" -> 22)
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      // Animate the number from 0 to value
      gsap.to(
        { val: 0 },
        {
          val: numericValue,
          duration: 1,
          ease: 'power2.out',
          delay: delay,
          onUpdate: function () {
            const currentVal = Math.round(this.targets()[0].val);
            // Preserve the suffix (%, min, etc.)
            const suffix = value.replace(/[0-9.]/g, '').trim();
            setDisplayValue(
              suffix ? `${currentVal}${suffix.startsWith(' ') ? suffix : ' ' + suffix}` : currentVal.toString()
            );
          },
          onComplete: () => {
            setIsComplete(true);
            setIsGlowing(true);
            // Remove glow after brief moment
            setTimeout(() => setIsGlowing(false), 400);
          },
        }
      );
    }
  }, [value, delay]);

  return (
    <Box>
      <Typography variant="body2" color="textSecondary">
        {title}
      </Typography>
      <Typography
        variant="h5"
        className={clsx({
          [classes.statGood]: isComplete && indication === 'good',
          [classes.statWarning]: isComplete && indication === 'warning',
          [classes.statSevere]: isComplete && indication === 'severe',
        })}
        style={{
          color: isComplete ? undefined : 'rgba(255, 255, 255, 0.5)',
          textShadow: isGlowing ? '0 0 25px currentColor, 0 0 50px currentColor' : 'none',
          transition: 'text-shadow 0.3s ease',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

// Helper functions to determine indication based on metric values
const getFcotsIndication = (fcots: number): 'good' | 'warning' | 'severe' => {
  if (fcots >= 90) return 'good';
  if (fcots >= 80) return 'warning';
  return 'severe';
};

const getTurnoverIndication = (turnover: number): 'good' | 'warning' | 'severe' => {
  if (turnover <= 20) return 'good';
  if (turnover <= 25) return 'warning';
  return 'severe';
};

const getUtilizationIndication = (utilization: number): 'good' | 'warning' | 'severe' => {
  if (utilization >= 85) return 'good';
  if (utilization >= 75) return 'warning';
  return 'severe';
};

const Panel = React.forwardRef<
  HTMLDivElement,
  {
    title: string;
    fcots: number;
    turnover: number;
    utilization: number;
    cases: number;
    panelIndex?: number;
  }
>(({ title, fcots, turnover, utilization, cases, panelIndex = 0 }, ref) => {
  const classes = useStyles();
  const barRef = useRef<HTMLDivElement>(null);
  const [currentBorderColor, setCurrentBorderColor] = useState('rgba(255, 255, 255, 0.23)');

  // Determine panel border color based on all stats
  const fcotsIndication = getFcotsIndication(fcots);
  const turnoverIndication = getTurnoverIndication(turnover);
  const utilizationIndication = getUtilizationIndication(utilization);

  const allGood = fcotsIndication === 'good' && turnoverIndication === 'good' && utilizationIndication === 'good';
  const allSevere =
    fcotsIndication === 'severe' && turnoverIndication === 'severe' && utilizationIndication === 'severe';

  useEffect(() => {
    if (barRef.current) {
      // Start with scaleY at 0 (collapsed from bottom)
      gsap.set(barRef.current, { scaleY: 0, transformOrigin: 'bottom' });

      // Animate to full height from bottom to top
      gsap.to(barRef.current, {
        scaleY: 1,
        duration: 0.8,
        ease: 'power2.out',
        delay: 0.1,
      });
    }

    // Animate border color when numbers complete (delay + duration + glow start)
    const animationDelay = 0.2 + panelIndex * 0.05 + 1; // Start after numbers finish counting

    // Determine target color RGB values
    const targetColor = allGood
      ? { r: 1, g: 221, b: 82, a: 1 }
      : allSevere
        ? { r: 255, g: 53, b: 53, a: 1 }
        : { r: 255, g: 255, b: 255, a: 0.23 };

    // Animate from default gray to target color
    const colorObj = { r: 255, g: 255, b: 255, a: 0.23 };
    gsap.to(colorObj, {
      r: targetColor.r,
      g: targetColor.g,
      b: targetColor.b,
      a: targetColor.a,
      duration: 0.4,
      ease: 'power2.out',
      delay: animationDelay,
      onUpdate: () => {
        setCurrentBorderColor(
          `rgba(${Math.round(colorObj.r)}, ${Math.round(colorObj.g)}, ${Math.round(colorObj.b)}, ${colorObj.a})`
        );
      },
    });
  }, [panelIndex, allGood, allSevere]);

  return (
    <Paper ref={ref} variant="outlined" className={classes.panel} style={{ borderColor: currentBorderColor }}>
      <Typography>{title}</Typography>

      <Box display="flex" justifyContent="space-between">
        <Box className={classes.statStack}>
          <SmallStat
            title="FCOTS"
            value={`${fcots}%`}
            indication={getFcotsIndication(fcots)}
            delay={0.2 + panelIndex * 0.05}
          />
          <SmallStat
            title="Turnover"
            value={`${turnover} min`}
            indication={getTurnoverIndication(turnover)}
            delay={0.2 + panelIndex * 0.05}
          />
          <SmallStat
            title="Utilization"
            value={`${utilization}%`}
            indication={getUtilizationIndication(utilization)}
            delay={0.2 + panelIndex * 0.05}
          />
        </Box>

        <Box className={classes.chartWrapper}>
          <Typography>Cases</Typography>
          <Box className={classes.chart}>
            <Box className={classes.legend}>
              <Typography variant="inherit">2000</Typography>
              <Typography variant="inherit">1500</Typography>
              <Typography variant="inherit">1000</Typography>
              <Typography variant="inherit">500</Typography>
              <Typography variant="inherit">0</Typography>
            </Box>
            <div ref={barRef} className={classes.bar} style={{ height: `${Math.floor((cases / 2000) * 100)}%` }} />
          </Box>
          <Typography>1950</Typography>
        </Box>
      </Box>
    </Paper>
  );
});

Panel.displayName = 'Panel';

const GroupAdminAnalytics: FC<RouteComponentProps> = () => {
  const classes = useStyles();
  const panelsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Animate panels with stagger
    gsap.from(panelsRef.current, {
      opacity: 0,
      y: 20,
      duration: 0.6,
      stagger: 0.05,
      ease: 'power2.out',
      delay: 0.2,
    });
  }, []);

  // Calculate stats from mockData
  const calculateStats = (metric: 'fcots' | 'turnover' | 'utilization' | 'cases') => {
    const values = mockData.map(item => item[metric]);
    const average = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const best = metric === 'turnover' ? Math.min(...values) : Math.max(...values);
    const worst = metric === 'turnover' ? Math.max(...values) : Math.min(...values);
    const bestIndex = values.indexOf(best);
    const worstIndex = values.indexOf(worst);
    return { average, best, worst, bestIndex, worstIndex };
  };

  const fcotStats = calculateStats('fcots');
  const turnoverStats = calculateStats('turnover');
  const utilizationStats = calculateStats('utilization');
  const casesStats = calculateStats('cases');

  // Helper function to get color from indication
  const getIndicationColor = (indication?: 'good' | 'warning' | 'severe'): string => {
    switch (indication) {
      case 'good':
        return '#4ade80'; // Bright green
      case 'warning':
        return '#fb923c'; // Bright orange
      case 'severe':
        return '#f87171'; // Bright red
      default:
        return '#22d3ee'; // Bright cyan for cases (no indication)
    }
  };

  // Function to flash a panel's outline
  const flashPanel = (index: number, color: string) => {
    const panel = panelsRef.current[index];
    if (panel) {
      // Scroll panel into view if not fully visible
      panel.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      gsap.fromTo(
        panel,
        { outline: '0px solid transparent' },
        {
          outline: `5px solid ${color}`,
          duration: 0.3,
          repeat: 5,
          yoyo: true,
          ease: 'power2.inOut',
          onComplete: () => {
            // Remove outline after animation
            gsap.to(panel, {
              outline: '0px solid transparent',
              duration: 0.2,
            });
          },
        }
      );
    }
  };

  return (
    // @ts-ignore
    <DarkThemeProviderWrapper>
      <Paper>
        <Box p={4}>
          <Box mb={3}>
            <Typography variant="h3">Group Performance Summary</Typography>
            <Typography color="textSecondary">Q3 2025 Reporting Period</Typography>
          </Box>

          <Box display="flex" className={classes.mainStats}>
            <StatBox
              title="FCOTS (%)"
              average={fcotStats.average}
              best={fcotStats.best}
              worst={fcotStats.worst}
              bestIndication={getFcotsIndication(fcotStats.best)}
              worstIndication={getFcotsIndication(fcotStats.worst)}
              onBestClick={() =>
                flashPanel(fcotStats.bestIndex, getIndicationColor(getFcotsIndication(fcotStats.best)))
              }
              onWorstClick={() =>
                flashPanel(fcotStats.worstIndex, getIndicationColor(getFcotsIndication(fcotStats.worst)))
              }
            />
            <StatBox
              title="Turnover (min)"
              average={turnoverStats.average}
              best={turnoverStats.best}
              worst={turnoverStats.worst}
              bestIndication={getTurnoverIndication(turnoverStats.best)}
              worstIndication={getTurnoverIndication(turnoverStats.worst)}
              onBestClick={() =>
                flashPanel(turnoverStats.bestIndex, getIndicationColor(getTurnoverIndication(turnoverStats.best)))
              }
              onWorstClick={() =>
                flashPanel(turnoverStats.worstIndex, getIndicationColor(getTurnoverIndication(turnoverStats.worst)))
              }
            />
            <StatBox
              title="Utilization (%)"
              average={utilizationStats.average}
              best={utilizationStats.best}
              worst={utilizationStats.worst}
              bestIndication={getUtilizationIndication(utilizationStats.best)}
              worstIndication={getUtilizationIndication(utilizationStats.worst)}
              onBestClick={() =>
                flashPanel(
                  utilizationStats.bestIndex,
                  getIndicationColor(getUtilizationIndication(utilizationStats.best))
                )
              }
              onWorstClick={() =>
                flashPanel(
                  utilizationStats.worstIndex,
                  getIndicationColor(getUtilizationIndication(utilizationStats.worst))
                )
              }
            />
            <StatBox
              title="Cases"
              average={casesStats.average}
              best={casesStats.best}
              worst={casesStats.worst}
              onBestClick={() => flashPanel(casesStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(casesStats.worstIndex, getIndicationColor())}
            />
          </Box>
        </Box>
      </Paper>

      <Box display="flex" className={classes.panels}>
        {mockData.map((item, index) => (
          <Panel
            key={item.hospitalName}
            ref={el => (panelsRef.current[index] = el)}
            title={item.hospitalName}
            fcots={item.fcots}
            turnover={item.turnover}
            utilization={item.utilization}
            cases={item.cases}
            panelIndex={index}
          />
        ))}
      </Box>
    </DarkThemeProviderWrapper>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexShrink: 0,
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  statBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  mainStats: {
    display: 'grid',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
  panels: {
    padding: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(6, 1fr)',
    },
  },
  panel: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderWidth: 3,
  },
  statStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  statGood: {
    color: '#01dd52', // Bright green (Tailwind green-400)
  },
  statWarning: {
    color: '#ff8c2d', // Bright orange (Tailwind orange-400)
  },
  statSevere: {
    color: '#ff3535', // Bright red (Tailwind red-400)
  },
  chartWrapper: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  chart: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary,
    fontSize: 10,
    textAlign: 'left',
    marginLeft: -theme.spacing(3),
  },
  bar: {
    width: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',

      '& > *': {
        marginLeft: 0,
        width: '100%',
      },

      '& > a': {
        marginRight: '0.5rem',
      },
    },
  },
}));

export default GroupAdminAnalytics;
